import { useRef, useState } from 'react';

const INITIAL_FORM = { name: '', email: '', company: '', phone: '', hasMIS: '', message: '', subscribe: false };
const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/contact`;
const FALLBACK_ERROR = 'Sorry, something went wrong. Please try again or email us directly at info@imprint-mis.co.uk';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

export const isTurnstileEnabled = Boolean(TURNSTILE_SITE_KEY);
export const turnstileSiteKey = TURNSTILE_SITE_KEY;

/**
 * Encapsulates contact form state, change handler, submission, and Turnstile ref.
 * If Turnstile is enabled, the token is fetched from the widget ref and sent
 * along with the payload. Otherwise, submissions proceed without a token
 * (backend fail-open in development).
 */
export const useContactForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const turnstileRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const submitContact = async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    let turnstileToken = null;
    if (isTurnstileEnabled) {
      turnstileToken = turnstileRef.current?.getResponse() || null;
      if (!turnstileToken) {
        setSubmitStatus({
          type: 'error',
          message: 'CAPTCHA is still loading. Please try again in a moment.',
        });
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          hasMIS: formData.hasMIS,
          message: formData.message,
          subscribe: formData.subscribe,
          turnstileToken,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send message');
      }
      setSubmitStatus({
        type: 'success',
        message: data.message || "Thank you for your message! We'll be in touch soon.",
      });
      setFormData(INITIAL_FORM);
      turnstileRef.current?.reset();
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.message || FALLBACK_ERROR,
      });
      turnstileRef.current?.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitContact();
  };

  return { formData, isSubmitting, submitStatus, handleChange, handleSubmit, turnstileRef };
};
